import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'

import paymentEvents from '@/utils/gaEvents/paymentEvents'

import Shared from '@/shared'

import { requestVerifyCode, requestAuthGuest, requestAuthCode } from '@/api/client/purchase'
import { ClientRequestError } from '@/lib/axiosClient'

export interface PurchasePhoneNumViewModel {
  texts: object
  states: {
    phoneNum: string
    authNum: string
    seconds: number
    authCode: number
    token: string
    guestSeq: number
    autoFocus: boolean

    isValidate: boolean
    isSend: boolean
    isAuth: boolean | null
    isConfirmOpen: boolean

    errorText: string
    toastText: string
    isErrorToast: boolean
    isToastOpen: boolean
  }
  setters: {
    setPhoneNum: Dispatch<SetStateAction<string>>
    setIsValidate: Dispatch<SetStateAction<boolean>>
    setIsSend: Dispatch<SetStateAction<boolean>>
    setSeconds: Dispatch<SetStateAction<number>>
    setIsToastOpen: Dispatch<SetStateAction<boolean>>
    setIsErrorToast: Dispatch<SetStateAction<boolean>>
    setAuthNum: Dispatch<SetStateAction<string>>
    setIsAuth: Dispatch<SetStateAction<boolean | null>>
    setIsConfirmOpen: Dispatch<SetStateAction<boolean>>

    onChangeAuthNumValue: (e: React.ChangeEvent<HTMLInputElement>) => void
    onChangePhoneNumValue: (e: React.ChangeEvent<HTMLInputElement>) => void
    onClickPhoneNextBtn: () => void
    requestAuthCodeAgain: () => void
    handleSendAuthCode: () => void
    showPurchaseInfo: () => void
    closePurchaseInfo: () => void
  }
  handlers: {
    expirationTime: (seconds: number) => string
    onClickHeaderBackBtn: () => void
  }
}

export interface usePurchasePhoneNumVewModelProps {
  isPartner: boolean
}

const usePurchasePhoneNumVewModel = ({ isPartner }: usePurchasePhoneNumVewModelProps): PurchasePhoneNumViewModel => {
  const setStep = Shared.States.usePurchaseStep((state) => state.setPurchaseStep)
  const [phoneNum, setPhoneNum] = useState('')
  const [authNum, setAuthNum] = useState('')
  const [seconds, setSeconds] = useState(180)
  const [token, setToken] = useState('')
  const [authCode, setAuthCode] = useState(0)

  const [isValidate, setIsValidate] = useState(false)
  const [isAuth, setIsAuth] = useState<boolean | null>(null)
  const [isSend, setIsSend] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const [errorText, setErrorText] = useState('')
  const [toastText, setToastText] = useState('')

  const [isErrorToast, setIsErrorToast] = useState(false)
  const [isToastOpen, setIsToastOpen] = useState(false)

  const { guestSeq } = Shared.States.useStorageStore()
  const autoFocus = isConfirmOpen ? false : true

  useEffect(() => {
    if (isSend) {
      if (seconds > 0) {
        const timerId = setInterval(() => {
          setSeconds(seconds - 1)
        }, 1000)
        return () => clearInterval(timerId)
      }
    }
  }, [seconds, isSend])

  const onChangePhoneNumValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNum(e.target.value)
    phoneValidation(e.target.value)
  }

  const onChangeAuthNumValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthNum(e.target.value)
  }

  const phoneValidation = (num: string) => {
    const isPhoneValidate = /^01([0])([0-9]{8})$/.test(num)
    setIsValidate(isPhoneValidate)
  }

  const onClickPhoneNextBtn = async () => {
    setToastText('')
    if (authCode && isValidate) {
      try {
        const authPayload = {
          guestSeq: guestSeq
        }
        const verifyCodePayload = {
          authCodeSeq: authCode,
          code: authNum,
          phone: phoneNum
        }
        await requestVerifyCode(verifyCodePayload)
        const { data } = await requestAuthGuest(authPayload)
        setToken(data.accessToken)
        setIsAuth(true)
        setIsConfirmOpen(true)
        setSeconds(180)
        setIsSend(false)
        setAuthNum('')
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          if (err.code !== 10000) {
            setErrorText(err.message)
            setIsAuth(false)
          } else {
            setIsErrorToast(true)
            setToastText(err.message)
          }
        } else {
          throw err
        }
      }
    }
  }

  const requestAuthCodeAgain = async () => {
    setSeconds(180)
    setAuthNum('')
    setToastText('')
    if (phoneNum) {
      setIsAuth(null)
      try {
        const payload = {
          phone: phoneNum
        }
        const { data } = await requestAuthCode(payload)
        setIsToastOpen(true)
        setAuthCode(data.authCodeSeq)
      } catch (err: unknown) {
        setIsSend(false)
        setIsAuth(false)
        if (err instanceof ClientRequestError) {
          setIsErrorToast(true)
          setToastText(err.message)
          setErrorText('')
        } else {
          throw err
        }
      }
    }
  }

  const handleSendAuthCode = async () => {
    setIsSend((prev) => !prev)
    try {
      const payload = {
        phone: phoneNum
      }
      const { data } = await requestAuthCode(payload)
      setAuthCode(data.authCodeSeq)
    } catch (err: unknown) {
      if (err instanceof ClientRequestError) {
        setIsErrorToast(true)
        setToastText(err.message)
      } else {
        throw err
      }
    }
  }

  const expirationTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
  }

  const showPurchaseInfo = () => {
    if (isPartner) {
      paymentEvents.partner.onLoadPhoneAuth()
    } else {
      paymentEvents.shared.onLoadPhoneAuth()
    }

    setIsConfirmOpen((prev) => !prev)
    setStep(4)
  }

  const closePurchaseInfo = () => {
    setIsSend(false)
    setIsConfirmOpen((prev) => !prev)
  }

  const onClickHeaderBackBtn = () => {
    setIsSend(false)
    setSeconds(180)
    setPhoneNum('')
    setAuthNum('')
    setStep(2)
    setIsAuth(null)
  }

  return {
    texts: {},
    states: {
      phoneNum,
      authNum,
      isValidate,
      isSend,
      seconds,
      token,
      authCode,
      guestSeq,
      autoFocus,
      isAuth,
      isConfirmOpen,
      errorText,
      toastText,
      isErrorToast,
      isToastOpen
    },
    setters: {
      setPhoneNum,
      setIsValidate,
      setIsSend,
      setIsToastOpen,
      setIsErrorToast,
      setSeconds,
      setAuthNum,
      setIsAuth,
      setIsConfirmOpen,

      onChangePhoneNumValue,
      onChangeAuthNumValue,
      onClickPhoneNextBtn,

      requestAuthCodeAgain,

      handleSendAuthCode,
      showPurchaseInfo,
      closePurchaseInfo
    },
    handlers: {
      expirationTime,
      onClickHeaderBackBtn
    }
  }
}

export default usePurchasePhoneNumVewModel
